import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { gtag } from '@guuru/analytics-web';
import { ForbiddenPage } from '@guuru/react-error-pages';

const fireTracking = () => {
  gtag.event('page_view', {
    page_path: window.location.pathname + window.location.search,
  });
};

const checkForUpdates = () => {
  const REFRESH_INTERVAL_MS = process.env.REFRESH_INTERVAL_MS || 5 * 60 * 1000;

  let lastVersionCheckAt = 0;
  let lastVersionModifiedDate = 0;

  if (
    process.env.NODE_ENV !== 'production'
    || Date.now() - lastVersionCheckAt < Number(REFRESH_INTERVAL_MS)
  ) {
    return;
  }

  fetch('/', { method: 'HEAD' })
    .then(({ headers, ok }) => {
      if (!ok) throw new Error('Failed to fetch last-modified date');
      return headers.get('last-modified');
    })
    .then((lastModifiedHeader) => {
      if (!lastModifiedHeader) return;
      const lastModifiedDate = new Date(lastModifiedHeader).getTime();
      if (lastModifiedDate > lastVersionModifiedDate) {
        window.location.reload();
      }
      lastVersionModifiedDate = lastModifiedDate;
      lastVersionCheckAt = Date.now();
    })
    .catch(() => {});
};

interface Session {
  uid?: string;
  isAccountVerified: boolean;
  isTosAccepted: boolean;
}

interface RequireAuthProps {
  canView?: boolean;
  children: React.ReactNode;
  session: Session;
}

const RequireAuth = function ({
  session,
  children,
  canView = true,
}: RequireAuthProps) {
  const location = useLocation();

  if (!session.uid) {
    return <Navigate replace to="/signin" state={{ from: location }} />;
  }

  if (!session.isAccountVerified) return <Navigate replace to="/verify" />;

  if (!canView) {
    return <ForbiddenPage />;
  }

  if (!session.isTosAccepted) return <Navigate replace to="/acceptTos" />;

  fireTracking();
  checkForUpdates();

  return children;
};

export default RequireAuth;
